import * as storage from "./storage";

/**
 * Tablet-side auth state. Three persistent values:
 *   - kioskId          — the device's row id
 *   - institutionId    — for resolve / scan / inventory scoping (also in JWT)
 *   - apiKey           — long-lived secret, exchanged for short-lived JWTs
 *
 * The kioskJwt itself is also persisted (so a reload doesn't immediately
 * round-trip /auth/session) but is treated as cache: if it's expired or
 * the heartbeat says the device is revoked, we wipe and re-auth.
 *
 * **Threat model: localStorage + XSS.**
 *
 * The apiKey is the long-lived credential. localStorage is JS-readable, so
 * any XSS landing on this origin (kiosk.medicoach.co.za) can exfiltrate
 * the apiKey and impersonate the kiosk from anywhere until an admin
 * notices and revokes (heartbeat-driven, ≤30s after revoke).
 *
 * The deployment contract assumes:
 *   1. The PWA is the ONLY page allowed on the tablet's browser
 *      (Knox kiosk-mode, Fully Kiosk Browser, or equivalent — see
 *      docs/kiosk-hardware-setup.md).
 *   2. The kiosk-mode browser blocks navigation to other URLs and
 *      installation of extensions.
 *   3. The kiosk URL has no untrusted JS surface (no third-party
 *      embeds, no user-content rendering).
 *
 * Under those constraints there is no path for XSS to exist — this is
 * essentially a single-page native app delivered via the web. Document
 * any future deviation (e.g. embedding marketing iframes, rendering
 * player notes) as a security-relevant change requiring threat-model
 * review. See docs/kiosk-architecture.md § Authentication for the full
 * model.
 */

interface PairedDevice {
  kioskId: string;
  institutionId: string;
  apiKey: string;
}

interface CachedJwt {
  token: string;
  expiresAt: number;
}

const KEY_KIOSK_ID = "kioskId";
const KEY_INSTITUTION_ID = "institutionId";
const KEY_API_KEY = "apiKey";
const KEY_JWT = "jwt";
const KEY_JWT_EXPIRES = "jwtExpires";

export const getPairedDevice = (): PairedDevice | null => {
  const kioskId = storage.get(KEY_KIOSK_ID);
  const institutionId = storage.get(KEY_INSTITUTION_ID);
  const apiKey = storage.get(KEY_API_KEY);
  if (!kioskId || !institutionId || !apiKey) return null;
  return { kioskId, institutionId, apiKey };
};

export const getCachedJwt = (): CachedJwt | null => {
  const token = storage.get(KEY_JWT);
  const expiresAt = storage.get(KEY_JWT_EXPIRES);
  if (!token || !expiresAt) return null;
  const exp = parseInt(expiresAt, 10);
  if (Number.isNaN(exp)) return null;
  return { token, expiresAt: exp };
};

export const setCachedJwt = (token: string, expiresInSeconds: number): void => {
  const expiresAt = Date.now() + expiresInSeconds * 1000;
  storage.set(KEY_JWT, token);
  storage.set(KEY_JWT_EXPIRES, String(expiresAt));
};

export const clearCachedJwt = (): void => {
  storage.remove(KEY_JWT);
  storage.remove(KEY_JWT_EXPIRES);
};

export const setPairedDevice = (device: PairedDevice): void => {
  storage.set(KEY_KIOSK_ID, device.kioskId);
  storage.set(KEY_INSTITUTION_ID, device.institutionId);
  storage.set(KEY_API_KEY, device.apiKey);
  // Drop any JWT cached against the previous pairing. Without this,
  // `ensureToken` would happily keep serving the old JWT for up to an
  // hour — and the JWT's `kioskId` claim is load-bearing on the server
  // (every kiosk-scoped row is keyed by it). A stale JWT after re-pair
  // means session-start writes under the OLD kioskID and the next
  // scan-item, using the same stale JWT against an existing session
  // for the NEW kioskID, hits "Kiosk session not found".
  clearCachedJwt();
};

/** Wipe everything — used on revocation or explicit unpair. */
export const unpair = (): void => {
  storage.wipe();
};

/** True if the cached JWT has > 60s of life remaining. */
export const isJwtFresh = (jwt: CachedJwt): boolean =>
  jwt.expiresAt - Date.now() > 60_000;
